import { useMemo } from "react";
import { format, isSameDay } from "date-fns";
import type { AgendaSettings, AppointmentStatus } from "@bookdental/schemas";
import { resolveCardColor } from "@/lib/agenda-colors";

type Appt = {
  id: string;
  patientName: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  color?: string | null;
  date: Date | string;
};

type Props = {
  date: Date;
  appointments: Appt[];
  settings: AgendaSettings;
  onAppointmentClick?: (id: string) => void;
};

export function MobileDayView({ date, appointments, settings, onAppointmentClick }: Props) {
  const items = useMemo(
    () =>
      appointments
        .filter((a) => isSameDay(new Date(a.date), date))
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [appointments, date],
  );

  if (items.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-16 text-center text-sm text-zinc-500">
        Nenhuma consulta em {format(date, "dd/MM")}.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-zinc-200 bg-white">
      {items.map((a) => {
        const color = resolveCardColor(
          { status: a.status, color: a.color, patientName: a.patientName },
          settings.cardStyle,
        );
        const isCancelled = a.status === "CANCELLED";
        return (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => onAppointmentClick?.(a.id)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-zinc-50"
            >
              <span
                className="h-10 w-1 shrink-0 rounded-full"
                style={{ background: color }}
              />
              <div className="min-w-0 flex-1">
                <div
                  className={`truncate text-[15px] font-semibold text-zinc-900 ${
                    isCancelled ? "line-through opacity-60" : ""
                  }`}
                >
                  {a.patientName}
                </div>
                <div className="mt-0.5 text-[13px] tabular-nums text-zinc-500">
                  {a.startTime} – {a.endTime}
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
